import React, { useEffect, useState } from 'react';
import { istDateTime } from '../lib/ist';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, KeyRound, CheckCircle2, AlertCircle, Phone, Copy } from 'lucide-react';
import { API, useAuth, canEdit } from '../App';

const LatePrints = () => {
  const { token, user } = useAuth();
  const editable = canEdit(user, 'payments');
  const [orders, setOrders] = useState([]);
  const [resolved, setResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [breaker, setBreaker] = useState(null);
  const [copiedCode, setCopiedCode] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [oRes, bRes] = await Promise.all([
        axios.get(`${API}/admin/payments/late-paid`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { resolved },
        }),
        axios
          .get(`${API}/admin/payments/circuit-breaker`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: null })),
      ]);
      setOrders(oRes.data.orders || []);
      setBreaker(bRes.data);
    } catch (e) {
      toast.error('Failed to load late prints');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved]);

  const issueCode = async (order) => {
    setIssuing(order.order_id);
    try {
      const r = await axios.post(
        `${API}/admin/payments/${order.order_id}/issue-redemption-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Code ${r.data.redemption_code} ${r.data.reused ? 'reused' : 'issued'}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to issue code');
    }
    setIssuing(null);
  };

  const resolve = async (order) => {
    if (!window.confirm(`Mark order ${order.order_id} as fulfilled?`)) return;
    try {
      await axios.post(
        `${API}/admin/payments/${order.order_id}/resolve-late-pay`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Marked resolved');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const triggerReconcile = async () => {
    setReconciling(true);
    try {
      const r = await axios.post(
        `${API}/admin/payments/reconcile`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = r.data;
      if (d.skipped_no_live) {
        toast.info('Live mode disabled — reconciler is a no-op until Razorpay keys are configured.');
      } else {
        toast.success(
          `Scanned ${d.scanned} · rescued ${d.rescued} · late ${d.late_paid} · errors ${d.errors}`,
        );
      }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconcile failed');
    }
    setReconciling(false);
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 1500);
    } catch {
      // ignore — non-secure context
    }
  };

  return (
    <div className="space-y-6" data-testid="late-prints-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-charcoal-900">Late Prints</h1>
          <p className="text-sm text-charcoal-800/60 mt-1">
            Customers who paid AFTER the booth's 5-minute timer expired. Issue them a redemption code and fulfil manually.
          </p>
        </div>
        <button
          onClick={triggerReconcile}
          disabled={reconciling}
          data-testid="late-prints-reconcile-btn"
          className="bg-charcoal-900 text-cream-50 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-charcoal-800 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} /> Run reconcile now
        </button>
      </div>

      {breaker && (
        <div
          className={`card-dashboard flex items-center justify-between gap-4 ${
            breaker.open ? 'border-2 border-red-500 bg-red-50/40' : ''
          }`}
        >
          <div>
            <div className="text-xs uppercase text-charcoal-800/50 tracking-wider">
              Razorpay client circuit
            </div>
            <div className="font-serif text-xl mt-1 flex items-center gap-2">
              {breaker.open ? (
                <>
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <span className="text-red-700">OPEN — short-circuiting upstream calls</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>Closed · {breaker.failures_in_window}/{breaker.threshold} failures in window</span>
                </>
              )}
            </div>
          </div>
          <div className="text-xs text-charcoal-800/50">
            {breaker.live_mode ? 'Live keys configured' : 'Mock mode (no live keys)'}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setResolved(false)}
          data-testid="late-prints-filter-pending"
          className={`px-3 py-1.5 rounded-full text-sm ${
            !resolved ? 'bg-charcoal-900 text-cream-50' : 'bg-cream-100 text-charcoal-800/70 hover:bg-cream-200'
          }`}
        >
          Awaiting fulfilment
        </button>
        <button
          onClick={() => setResolved(true)}
          data-testid="late-prints-filter-resolved"
          className={`px-3 py-1.5 rounded-full text-sm ${
            resolved ? 'bg-charcoal-900 text-cream-50' : 'bg-cream-100 text-charcoal-800/70 hover:bg-cream-200'
          }`}
        >
          Resolved
        </button>
        <button onClick={fetchAll} className="ml-auto text-sm text-charcoal-800/60 hover:text-charcoal-900 flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="card-dashboard p-0 overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-charcoal-800/40">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 text-charcoal-800/40">
            {resolved
              ? 'No resolved late-paid orders yet.'
              : 'All caught up — no late prints awaiting fulfilment.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="late-prints-table">
              <thead className="bg-cream-50">
                <tr className="text-left">
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Order</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Booth</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Amount</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Customer</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Reconciled</th>
                  <th className="px-4 py-3 text-xs font-medium text-charcoal-800/60 uppercase">Code</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-charcoal-900/5">
                {orders.map((o) => (
                  <tr key={o.order_id} className="hover:bg-cream-50">
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-charcoal-900">{o.order_id}</div>
                      <div className="text-[10px] text-charcoal-800/50">qty {o.quantity}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-charcoal-800/70 font-mono">
                      {(o.kiosk_id || '').slice(0, 8)}
                    </td>
                    <td className="px-4 py-3">₹{o.amount}</td>
                    <td className="px-4 py-3">
                      {o.customer_phone ? (
                        <a
                          href={`tel:${o.customer_phone}`}
                          className="text-charcoal-800 flex items-center gap-1 hover:underline"
                        >
                          <Phone className="w-3 h-3" /> {o.customer_phone}
                        </a>
                      ) : (
                        <span className="text-charcoal-800/30 text-xs">no phone</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-charcoal-800/60">
                      {o.reconciled_at ? istDateTime(o.reconciled_at) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {o.redemption_code ? (
                        <button
                          onClick={() => copyCode(o.redemption_code)}
                          className="font-mono text-base bg-amber-50 text-amber-900 px-2 py-1 rounded flex items-center gap-1.5 hover:bg-amber-100"
                          data-testid={`late-prints-code-${o.order_id}`}
                        >
                          {o.redemption_code}
                          {copiedCode === o.redemption_code ? (
                            <CheckCircle2 className="w-3 h-3 text-green-600" />
                          ) : (
                            <Copy className="w-3 h-3 opacity-60" />
                          )}
                        </button>
                      ) : (
                        <span className="text-charcoal-800/30 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {!resolved && editable && (
                        <div className="flex gap-2">
                          {!o.redemption_code && (
                            <button
                              onClick={() => issueCode(o)}
                              disabled={issuing === o.order_id}
                              data-testid={`late-prints-issue-${o.order_id}`}
                              className="text-xs px-3 py-1.5 rounded-lg border border-charcoal-900/20 hover:bg-cream-100 flex items-center gap-1 disabled:opacity-50"
                            >
                              <KeyRound className="w-3 h-3" /> Issue code
                            </button>
                          )}
                          <button
                            onClick={() => resolve(o)}
                            data-testid={`late-prints-resolve-${o.order_id}`}
                            className="text-xs px-3 py-1.5 rounded-lg bg-charcoal-900 text-cream-50 hover:bg-charcoal-800 flex items-center gap-1"
                          >
                            <CheckCircle2 className="w-3 h-3" /> Mark resolved
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LatePrints;
